/**
 * CSP Debugger Utility
 *
 * This utility helps debug Content Security Policy violations in development.
 * It logs CSP violations to the console and provides helpful information.
 */

import { logger } from "./logger";
import { trackEvent } from "./analytics";

interface CSPViolation {
  documentURI: string;
  violatedDirective: string;
  originalPolicy: string;
  blockedURI?: string;
  sourceFile?: string;
  lineNumber?: number;
  columnNumber?: number;
}

// Track seen violations to prevent spam
const seenViolations = new Set<string>();
const violationTimestamps = new Map<string, number>();
const MAX_VIOLATIONS = 50; // Prevent memory leaks in long-running dev sessions
const RATE_LIMIT_MS = 1000; // Only log once per second per violation type

export function initCSPDebugger() {
  if (typeof window === "undefined" || process.env.NODE_ENV !== "development") {
    return;
  }

  // Listen for CSP violations
  document.addEventListener("securitypolicyviolation", event => {
    const violation: CSPViolation = {
      documentURI: event.documentURI,
      violatedDirective: event.violatedDirective,
      originalPolicy: event.originalPolicy,
      blockedURI: event.blockedURI,
      sourceFile: event.sourceFile,
      lineNumber: event.lineNumber,
      columnNumber: event.columnNumber,
    };

    // Create a composite key for deduplication
    const violationKey = `${violation.violatedDirective}:${violation.blockedURI || "unknown"}`;
    const now = Date.now();
    const lastSeen = violationTimestamps.get(violationKey);

    // Rate limiting: only process if enough time has passed
    if (lastSeen && now - lastSeen < RATE_LIMIT_MS) {
      return;
    }

    // Check if we've seen this violation before
    const isNewViolation = !seenViolations.has(violationKey);

    if (isNewViolation) {
      // Prevent memory leaks by limiting tracked violations
      if (seenViolations.size >= MAX_VIOLATIONS) {
        // Clear oldest entries (simple FIFO)
        const firstKey = seenViolations.values().next().value;
        if (firstKey !== undefined) {
          seenViolations.delete(firstKey);
          violationTimestamps.delete(firstKey);
        }
      }

      seenViolations.add(violationKey);

      // Log to console only for new unique violations
      logger.warn("🚨 CSP Violation", {
        component: "CSPDebugger",
        metadata: {
          directive: violation.violatedDirective,
          blockedURI: violation.blockedURI,
          sourceFile: violation.sourceFile,
          lineNumber: violation.lineNumber,
          columnNumber: violation.columnNumber,
        },
      });
    }

    // Update timestamp for rate limiting
    violationTimestamps.set(violationKey, now);

    // Send to analytics (but only for new violations to prevent feedback loops)
    if (isNewViolation) {
      trackEvent("csp_violation", {
        violation_directive: violation.violatedDirective,
        blocked_uri: violation.blockedURI,
        source_file: violation.sourceFile,
        violation_count: 1,
      });
    }
  });

  // Log CSP policy on page load
  // CSP policy information is available in browser dev tools
}

// Auto-initialize in development
if (typeof window !== "undefined" && process.env.NODE_ENV === "development") {
  initCSPDebugger();
}
